function arnComponents() {
    class ArnTabs extends HTMLElement {
        connectedCallback() {
            this.tabList = this.querySelector(`[role="tablist"]`);
            this.tabButtons = this.querySelectorAll(`[role="tab"]`);
            this.tabPanels = this.querySelectorAll(`[role="tabpanel"]`);
            this.underlineEl = this.querySelector("[data-tabs-underline]");

            this.tabButtons.forEach(tb => tb.addEventListener("click", this.tabBtn_clickHandler));

            if (this.tabButtons.length > 0) {
                this.selectTab(this.tabButtons[0]);
            }
        }

        disconnectedCallback() {
            this.tabButtons.forEach(tb => tb.removeEventListener("click", this.tabBtn_clickHandler));
        }

        tabBtn_clickHandler = (event) => {
            this.selectTab(event.currentTarget);
        }

        selectTab(selTab) {
            this.tabButtons.forEach(tb => {
                const isCurrent = tb === selTab;
                tb.ariaSelected = isCurrent;
                tb.toggleAttribute("data-current", isCurrent);

                if (isCurrent) {
                    this.showPanel(tb.getAttribute("data-tabs-target"));
                    this.updateUnderline(tb.offsetLeft, tb.offsetTop + tb.offsetHeight, tb.offsetWidth);
                }
            });
        }

        showPanel(selector) {
            const target = selector ? this.querySelector(selector) : null;

            this.tabPanels.forEach(tp => {
                tp.toggleAttribute("data-current", tp === target);
            });
        }

        updateUnderline(left, top, width) {
            if (this.underlineEl) {
                this.underlineEl.style.left = `${left}px`;
                this.underlineEl.style.top = `${top - this.underlineEl.offsetHeight}px`;
                this.underlineEl.style.width = `${width}px`;
            }
        }
    }

    class ArnRangeSlider extends HTMLElement {
        connectedCallback() {
            this.inputMin = this.querySelector("input[data-slider-in='min']");
            this.inputMax = this.querySelector("input[data-slider-in='max']");
            this.track = this.querySelector("[data-slider-track]");
            this.range = this.querySelector("[data-slider-range]");
            this.labels = this.querySelectorAll("[data-slider-lb]");
            this.stepDisplay = this.querySelector("[data-step-display]");

            this.limitMin = Number(this.dataset.min) || 0;
            this.limitMax = Number(this.dataset.max) || 100;
            this.minRange = Number(this.dataset.minRange) || 0;
            this.stepGuides = Number(this.dataset.stepGuides) || 0;
            this.minVal = Number(this.inputMin?.value) || this.limitMin;
            this.maxVal = Number(this.inputMax?.value) || this.limitMax;

            if (this.inputMin) {
                this.inputMin.setAttribute("min", this.limitMin);
                this.inputMin.setAttribute("max", this.limitMax);
                this.inputMin.addEventListener("input", this.minInputHandler);
            }

            if (this.inputMax) {
                this.inputMax.setAttribute("min", this.limitMin);
                this.inputMax.setAttribute("max", this.limitMax);
                this.inputMax.addEventListener("input", this.maxInputHandler);
            }

            this.addEventListener("click", this.clickHandler);
            this.setCurrentAttr("min");
            this.updateStepDisplay();
            this.updateRange();
        }

        disconnectedCallback() {
            this.inputMin?.removeEventListener("input", this.minInputHandler);
            this.inputMax?.removeEventListener("input", this.maxInputHandler);
            this.removeEventListener("click", this.clickHandler);
        }

        clickHandler = (e) => {
            const currSlider = e.target.getAttribute("data-slider-in") || e.target.getAttribute("data-slider-lb");

            if (currSlider) {
                this.setCurrentAttr(currSlider);
            }
        }

        maxInputHandler = (e) => {
            const value = Math.max(Number(e.target.value), this.minVal + this.minRange);

            this.maxVal = value;
            e.target.value = value;

            this.setCurrentAttr("max");
            this.updateRange();
        }

        minInputHandler = (e) => {
            const value = Math.min(Number(e.target.value), this.maxVal - this.minRange);

            this.minVal = value;
            e.target.value = value;

            this.setCurrentAttr("min");
            this.updateRange();
        }

        getPercent(val) {
            return Math.round(((val - this.limitMin) / (this.limitMax - this.limitMin)) * 100);
        }

        setCurrentAttr(currentSlider) {
            this.inputMin?.toggleAttribute("data-current", currentSlider === "min");
            this.inputMax?.toggleAttribute("data-current", currentSlider === "max");

            this.labels.forEach(lb => {
                lb.toggleAttribute("data-current", lb.getAttribute("data-slider-lb") === currentSlider);
            });
        }

        updateRange() {
            const minPerc = this.getPercent(this.minVal);
            const maxPerc = this.getPercent(this.maxVal);

            if (this.range) {
                this.range.style.left = `${minPerc}%`;
                this.range.style.width = `${maxPerc - minPerc}%`;
            }

            this.labels.forEach(lb => {
                switch (lb.getAttribute("data-slider-lb")) {
                    case "min":
                        lb.style.left = `${minPerc}%`;
                        lb.textContent = this.minVal;
                        break;
                    case "max":
                        lb.style.left = `${maxPerc}%`;
                        lb.textContent = this.maxVal;
                        break;
                }
            });
        }

        updateStepDisplay() {
            const numStep = this.stepGuides;
            let stepHTML = "";

            if (!this.stepDisplay || !numStep) {
                return;
            }

            for (let i = 0; i <= numStep; i++) {
                const primary = i === 0 || i === numStep / 2 || i === numStep;
                stepHTML += `<span data-step-index="${i}" ${primary ? "data-step-primary" : ""}></span>`;
            }

            this.stepDisplay.innerHTML = stepHTML;
        }
    }

    class ArnTextField extends HTMLElement {
        connectedCallback() {
            this.input = this.querySelector('input');
            this.textArea = this.querySelector('textarea');
            this.emptyField = this.querySelector('.js-empty-field')
            this.characterCounter = this.querySelector('.js-character-counter');
            this.dropdownArntions = this.querySelectorAll('.js-dropdown-option');
            this.showPickerBtn = this.querySelector(".js-show-picker");
            this.showPassBtn = this.querySelector(".js-show-pass");

            // Listen to changes on input to show/hide clear button
            this.input?.addEventListener('input', this.inputHandler);

            // Clear button click event
            this.emptyField?.addEventListener('pointerdown', this.emptyFieldHandler);
            this.showPickerBtn?.addEventListener("click", this.pickerBtnHandler);
            this.showPassBtn?.addEventListener("click", this.showPassBtnHandler);

            // Text area input listener (to count characters)
            if (this.textArea && this.textArea.maxLength > 0) {
                this.textArea.addEventListener('input', this.textAreaHandler);
            }

            // Dropdown options select listener
            this.dropdownArntions.forEach((span) => {
                span.addEventListener("pointerdown", this.dropdownArntionHandler);
            });
        }

        disconnectedCallback() {
            this.input?.removeEventListener('input', this.inputHandler);
            this.textArea?.removeEventListener('input', this.textAreaHandler);
            this.emptyField?.removeEventListener('pointerdown', this.emptyFieldHandler);
            this.showPickerBtn?.removeEventListener("click", this.pickerBtnHandler);
            this.showPassBtn?.removeEventListener("click", this.showPassBtnHandler);

            this.dropdownArntions.forEach((span) => {
                span.removeEventListener("pointerdown", this.dropdownArntionHandler);
            });
        }

        inputHandler = () => {
            this.emptyField.style.display = this.input.value.length > 0 ? "block" : "none";
        }

        textAreaHandler = () => {
            const nrOfCharacters = this.textArea.value.length;
            this.characterCounter.textContent = nrOfCharacters;
        }

        emptyFieldHandler = () => {
            if (this.input) {
                this.input.value = "";
                this.input.closest("form")?.dispatchEvent(new Event('input'));
                this.emptyField.style.display = "none";
                this.input.dispatchEvent(new CustomEvent("empty"));
            }
        }

        dropdownArntionHandler = (event) => {
            const value = event.target?.textContent;

            if (this.input && value) {
                this.input.value = value;
                this.inputHandler();
            }
        }

        pickerBtnHandler = () => {
            if (this.input && !this.input.disabled && !this.input.readOnly) {
                this.input.showPicker();
            }
        }

        showPassBtnHandler = () => {
            if (this.input) {
                const isPassword = this.input.type === "password";

                // Change input type
                this.input.type = isPassword ? "text" : "password";

                // Toggle icon visibility
                const eyeSlashIcon = this.showPassBtn.querySelector('.eye-slash-icon');
                const eyeIcon = this.showPassBtn.querySelector('.eye-icon');

                if (eyeSlashIcon && eyeIcon) {
                    if (isPassword) {
                        eyeSlashIcon.classList.add('tw-hidden');
                        eyeIcon.classList.remove('tw-hidden');
                    } else {
                        eyeSlashIcon.classList.remove('tw-hidden');
                        eyeIcon.classList.add('tw-hidden');
                    }
                }
            }
        }
    }

    class ArnSlideShow extends HTMLElement {
        connectedCallback() {
            const intervalSeconds = parseInt(this.dataset.interval) || 5;
            let currentIndex = 0;

            this.slides = this.querySelectorAll("[data-slide]");
            this.tooltip = this.querySelector("[data-ct-tooltip]");
            this.videoThumbnails = [...this.querySelectorAll("arn-video-with-thumbnail")];

            this.interval = setInterval(() => {
                if (this.videoThumbnails.length === 0 || this.videoThumbnails.every(vt => vt.isPaused)) {
                    currentIndex = currentIndex < this.slides.length - 1 ? currentIndex + 1 : 0;
                    this.showSlide(currentIndex);
                }
            }, intervalSeconds * 1000);

            if (this.tooltip) {
                this.addEventListener("mousemove", this.mouseMoveHandler);
                this.addEventListener("mouseleave", this.mouseExitHandler);
            }

            this.showSlide(currentIndex);
        }

        disconnectedCallback() {
            clearInterval(this.interval);

            if (this.tooltip) {
                this.removeEventListener("mousemove", this.mouseMoveHandler);
                this.removeEventListener("mouseleave", this.mouseExitHandler);
            }
        }

        showSlide(showIndex) {
            for (let i = 0; i < this.slides.length; i++) {
                const isCurrent = i === showIndex;
                const slide = this.slides[i];

                slide.style.display = isCurrent ? "block" : "none";

                if (isCurrent) {
                    this.updateTooltip(slide.getAttribute("data-tooltip-text"));
                }
            }
        }

        updateTooltip(text = "") {
            const isEmpty = !text || text.trim() === "";

            if (this.tooltip) {
                this.tooltip.textContent = text;
                this.tooltip.toggleAttribute("data-visible", !isEmpty);

                if (isEmpty) {
                    this.tooltip.style.display = "none";
                }
            }
        }

        mouseMoveHandler = (event) => {
            if (this.tooltip?.hasAttribute("data-visible")) {
                this.tooltip.style.left = `${event.clientX + 20}px`;
                this.tooltip.style.top = `${event.clientY + 20}px`;
                this.tooltip.style.display = "block";
            }
        }

        mouseExitHandler = () => {
            if (this.tooltip) {
                this.tooltip.style.display = "none";
            }
        }
    }

    class ArnCountdown extends HTMLElement {
        connectedCallback() {
            this.originalHTML = this.innerHTML;
            this.cdPlaceholder = this.dataset.cdPlaceholder || "%countdown%";

            this.endDate = this.parseDate(this.dataset.endTime);
            this.interval = setInterval(this.runCountdownInterval.bind(this), 1000);

            this.runCountdownInterval();
            this.classList.replace("tw-hidden", "tw-block");
        }

        disconnectedCallback() {
            clearInterval(this.interval);
        }

        displayError(message) {
            if (window.Shopify?.designMode) {
                this.innerHTML = this.originalHTML.replace(this.cdPlaceholder, message);
            }

            console.error("Countdown:", message);
        }

        formatTime(time) {
            const hours = Math.floor(time / (1000 * 60 * 60));
            const minutes = Math.floor((time % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((time % (1000 * 60)) / 1000);

            const timeParts = [];

            if (hours > 0) {
                timeParts.push(`${hours}H`.padStart(3, '0'));
            }
            if (hours > 0 || minutes > 0) {
                timeParts.push(`${minutes}M`.padStart(3, '0'));
            }
            if (hours > 0 || minutes > 0 || seconds > 0) {
                timeParts.push(`${seconds}S`.padStart(3, '0'));
            }

            if (timeParts.length === 0) {
                clearInterval(this.interval);
                return '';
            }

            return timeParts.join(':');
        }

        parseDate(rawDate = "") {
            const dateSetting = rawDate?.split(" ");
            let dateString = dateSetting ? dateSetting[0] : null;

            if (!dateString || dateString.trim() === "") {
                this.displayError("No date provided!");
                return -1;
            }

            if (dateSetting.length > 1) {
                dateString += "T" + dateSetting[1];
            }

            const parsedDate = new Date(dateString).getTime();

            if (isNaN(parsedDate)) {
                this.displayError("Invalid date!");
                return -1;
            }

            return parsedDate;
        }

        runCountdownInterval() {
            if (this.endDate !== -1 && this.originalHTML.includes(this.cdPlaceholder)) {
                this.updateCountdown();
            }
        }

        updateCountdown() {
            const now = Date.now();
            const timeRemaining = this.endDate - now;

            if (timeRemaining <= 0) {
                this.innerHTML = this.originalHTML.replace(this.cdPlaceholder, '');
                clearInterval(this.interval);
                return;
            }
            const formattedTime = this.formatTime(Math.max(0, timeRemaining));
            this.innerHTML = this.originalHTML.replace(this.cdPlaceholder, formattedTime);
        }
    }

    class ArnQuantitySelector extends HTMLElement {
        connectedCallback() {
            this.input = this.querySelector("input");
            this.quantityAdd = this.querySelector("[data-quantity-add]");
            this.quantitySub = this.querySelector("[data-quantity-sub]");
            this.quantityAdd?.addEventListener("click", this.quantityAddHandler);
            this.quantitySub?.addEventListener("click", this.quantitySubHandler);
        }

        quantityAddHandler = () => {
            this.input.value = Math.max(0, Number(this.input.value) + 1);
        }

        quantitySubHandler = () => {
            this.input.value = Math.max(0, Number(this.input.value) - 1);
        }
    }

    class ArnPasswordChecker extends HTMLElement {
        connectedCallback() {
            this.input = document.querySelector(this.dataset.input);
            this.inputConfirm = document.querySelector(this.dataset.inputConfirm);
            this.clearButton = this.input.closest("arn-text-field")?.querySelector(".js-empty-field");
            this.lengthOutput = this.querySelector("[data-password-checker='length']");
            this.uppercaseOutput = this.querySelector("[data-password-checker='uppercase']");
            this.lowercaseOutput = this.querySelector("[data-password-checker='lowercase']");
            this.numberOutput = this.querySelector("[data-password-checker='number']");
            this.specialOutput = this.querySelector("[data-password-checker='special']");
            this.passwordVerified = false;
            this.waitForInputs = false;
            this.requirements = {
                length: false,
                uppercase: false,
                lowercase: false,
                number: false,
                special: false,
                passwordConfirmed: false,
            }

            this.input?.addEventListener("input", this.checkPassword.bind(this));
            this.inputConfirm?.addEventListener("input", this.checkPassword.bind(this));
            this.clearButton?.addEventListener("pointerdown", this.checkPassword.bind(this));
        }

        checkPassword() {
            this.lengthCheck();
            this.uppercaseCheck();
            this.lowercaseCheck();
            this.numberCheck();
            this.specialCheck();
            // this.checkConfirmPassword();
            // this.toggleSubmitButton();
        }

        toggleSubmitButton() {
            const submitButton = this.closest("form").querySelector("[data-submit-form]");
            const isDisabled = Object.values(this.requirements).includes(false);
            this.passwordVerified = !isDisabled;
            if (!this.waitForInputs) submitButton.disabled = isDisabled;
        }

        lengthCheck() {
            const condition = this.input.value.length >= 8;
            this.checkCondition(condition, "length", this.lengthOutput);
        }

        uppercaseCheck() {
            const condition = /[A-Z]/.test(this.input.value);
            this.checkCondition(condition, "uppercase", this.uppercaseOutput);
        }

        lowercaseCheck() {
            const condition = /[a-z]/.test(this.input.value);
            this.checkCondition(condition, "lowercase", this.lowercaseOutput);
        }

        numberCheck() {
            const condition = /[0-9]/.test(this.input.value);
            this.checkCondition(condition, "number", this.numberOutput);
        }

        specialCheck() {
            const condition = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.input.value);
            this.checkCondition(condition, "special", this.specialOutput);
        }

        checkCondition(condition, key, output) {
            const verifCheck = output?.querySelector(".verification-check");
            const verifNeutral = output?.querySelector(".verification-neutral");

            if (condition) {
                this.requirements[key] = true;
                verifCheck?.classList.remove("tw-hidden");
                verifNeutral?.classList.add("tw-hidden");
            } else {
                this.requirements[key] = false;
                verifCheck?.classList.add("tw-hidden");
                verifNeutral?.classList.remove("tw-hidden");
            }
        }

        checkConfirmPassword() {
            if (!this.inputConfirm) return;

            const passConfirmed = this.input.value === this.inputConfirm.value;

            this.checkCondition(passConfirmed, "passwordConfirmed");

            if (this.input.value && this.inputConfirm.value) {
                this.inputConfirm.closest("arn-text-field")?.classList.toggle("tw-arn-textfield-error", !passConfirmed);
            }
        }
    }

    class ArnPasswordCheckerContainer extends HTMLElement {
        constructor() {
            super();
            this.checkerContainer = this.getElementsByClassName("password-checker-container")[0];
            this.passwordInput = document.getElementById(this.dataset.customerPassword);

            this.init();
        }

        init() {
            this.passwordInput.addEventListener("input", () => this.toggleVisibility());
        }

        toggleVisibility() {
            if (this.passwordInput.value.length > 0) {
                this.checkerContainer.classList.remove("tw-hidden");
            } else {
                this.checkerContainer.classList.add("tw-hidden");
            }
        }

        disconnectedCallback() {
            this.passwordInput.removeEventListener("input", this.toggleVisibility);
        }
    }

    class ArnForm extends HTMLElement {
        connectedCallback() {
            this.form = this.querySelector("form");
            this.submitButton = this.form.querySelector("[data-submit-form]");
            this.passwordChecker = this.form.querySelector("op-password-checker");
            this.allInputsFilled = false;

            if (!this.form || !this.submitButton) return;

            this.form.addEventListener("input", this.checkRequiredFields.bind(this));
        }

        checkRequiredFields() {
            this.allInputsFilled = Array.from(this.form.querySelectorAll('input[required]')).every(input => input.value.trim() !== '');
            this.toggleSubmitButton();
        }

        toggleSubmitButton() {
            if (this.passwordChecker) {
                this.submitButton.disabled = !(this.passwordChecker.passwordVerified && this.allInputsFilled);
            } else {
                this.submitButton.disabled = !this.allInputsFilled;
            }
        }
    }

    class ArnNewsletterForm extends HTMLElement {
        connectedCallback() {
            this.form = this.querySelector('form.newsletter-form') || this.querySelector('form');
            if (!this.form) return;

            this.emailField = this.querySelector('input[name="contact[email]"]');
            this.cryptoUrl = this.dataset.cryptoScript || "https://cdnjs.cloudflare.com/ajax/libs/crypto-js/4.2.0/crypto-js.min.js";
            this.thankYouUrl = this.dataset.thankYouUrl || '/pages/subscribe-thank-you';

            this._handleSubmit = this._handleSubmit.bind(this);
            this._handleTealiumLoaded = this._handleTealiumLoaded.bind(this);

            if (this.emailField) {
                this.form.addEventListener('submit', this._handleSubmit);
            }

            this._handlePostSubmit();
        }

        disconnectedCallback() {
            if (this.form) {
                this.form.removeEventListener('submit', this._handleSubmit);
            }
            document.removeEventListener('tealiumListenersLoaded', this._handleTealiumLoaded);
        }

        /* -----------------------------
         SUBMIT (before Shopify handles it)
        ----------------------------- */
        _handleSubmit() {
            const email = this.emailField?.value.toLowerCase().trim();
            if (!email) return;

            // Save only temporary email for post-redirect tracking
            sessionStorage.setItem("newsletterFormSubmitted", email);

            // UI loading state (safe for Shopify captcha)
            this._applyLoadingState();
        }

        _applyLoadingState() {
            this.classList.add('is-loading');

            const button = this.form.querySelector('button[type="submit"]');
            if (button) {
                button.disabled = true;
                button.setAttribute('aria-busy', 'true');
            }
        }

        /* -----------------------------
         AFTER SHOPIFY REDIRECT
        ----------------------------- */
        _handlePostSubmit() {
            const urlParams = new URLSearchParams(window.location.search);
            const isSuccessful = urlParams.get('customer_posted') === 'true';
            const sessionEmail = sessionStorage.getItem("newsletterFormSubmitted");

            if (!isSuccessful || !sessionEmail) return;

            // Newsletter confirmed => set real subscription flag
            sessionStorage.setItem("arn-newsletter-subscribed", "true");

            // Subscribing earns the gated promos on the spot, exactly like arriving from the
            // newsletter link: writes the cookie and the cart attribute before the thank-you
            // redirect, so the discount is live from the next page on.
            window.ArnPromoSession?.grant?.();

            // Clean URL (remove ?customer_posted=true)
            window.history.replaceState({}, document.title, window.location.pathname);

            // Wait for Tealium OR fallback after timeout
            let tealiumHandled = false;

            const fallbackTimeout = setTimeout(() => {
                if (!tealiumHandled) {
                    this._finalRedirect();
                }
            }, 1500); // fallback after 1.5s

            document.addEventListener('tealiumListenersLoaded', () => {
                if (tealiumHandled) return;
                tealiumHandled = true;
                clearTimeout(fallbackTimeout);
                this._handleTealiumLoaded();
            }, { once: true });
        }

        _handleTealiumLoaded() {
            const sessionEmail = sessionStorage.getItem("newsletterFormSubmitted");
            if (!sessionEmail) {
                this._finalRedirect();
                return;
            }

            Fetchutils.importScript(this.cryptoUrl)
                .then(() => {
                    publish(PUB_SUB_EVENTS.newsletterSubscription, {
                        Events_UserEmailSub: "1",
                        User_Email_MD5: CryptoJS.MD5(sessionEmail).toString(),
                        User_Email_SHA256: CryptoJS.SHA256(sessionEmail).toString()
                    });
                })
                .catch(error => {
                    console.error('Error loading CryptoJS:', error);
                })
                .finally(() => {
                    this._finalRedirect();
                });
        }

        _finalRedirect() {
            sessionStorage.removeItem("newsletterFormSubmitted");
            window.location.replace(this.thankYouUrl);
        }
    }

    class ArnVideoWithThumbnail extends HTMLElement {
        get isPaused() {
            return this.videoElement?.paused || this.dataset.playing === "false";
        }

        connectedCallback() {
            this.videoElement = this.querySelector('video');
            this.playPauseBtn = this.querySelector("[data-play-btn]");
            this.audioMuteBtn = this.querySelector("[data-mute-btn]");

            if (!this.videoElement || !this.playPauseBtn || !this.audioMuteBtn) return;

            this.videoElement.addEventListener("click", this.togglePlayPause.bind(this));
            this.playPauseBtn.addEventListener("click", this.togglePlayPause.bind(this));
            this.audioMuteBtn.addEventListener("click", this.toggleMute.bind(this));
        }

        togglePlayPause() {
            if (this.videoElement.paused) {
                window.pauseAllMedia();
                this.videoElement.play();
                this.dataset.playing = true;
            } else {
                this.videoElement.pause();
                this.dataset.playing = false;
            }
        }

        toggleMute() {
            if (this.videoElement.muted) {
                this.videoElement.muted = false;
                this.dataset.muted = false;

                if (this.dataset.playing === "hover") {
                    this.dataset.playing = true;
                }
            } else {
                this.videoElement.muted = true;
                this.dataset.muted = true;
            }
        }

    }

    class ArnModalTrigger extends HTMLElement {
        connectedCallback() {
            this.modal = document.querySelector(`#${this.dataset.modalId}`);
            this.trigger = this.querySelector(".js-modal-trigger") || this.querySelector("button");

            this.animation = this.hasAttribute("data-animate");
            this.animateBody = this.dataset.animateBody === "true";

            this.trigger?.addEventListener("click", this.triggerClickHandler);

            if (this.animateBody) {
                document.body.classList.add("tw-transition-transform");
                document.body.classList.add("tw-duration-300");
            }

            if (this.animation) {
                this.modal?.addEventListener('close', this.modalCloseHandler);
            }
        }

        disconnectedCallback() {
            this.trigger?.removeEventListener("click", this.triggerClickHandler);

            if (this.animateBody) {
                document.body.classList.remove("tw-transition-transform");
                document.body.classList.remove("tw-duration-300");
            }

            if (this.animation) {
                this.modal?.removeEventListener('close', this.modalCloseHandler);
            }
        }

        triggerClickHandler = () => {
            this.modal?.showModal();

            if (!this.animation) return;

            this.modal?.classList.remove("tw-translate-x-full")
            if (this.animateBody) document.body.style.transform = "translateX(-30px)";
        }

        modalCloseHandler = () => {
            this.modal?.classList.add("tw-translate-x-full")
            if (this.animateBody) document.body.style.transform = "translateX(0px)";
        }
    }

    class ArnModalCloser extends HTMLElement {
        connectedCallback() {
            this.modal = this.closest("dialog");
            this.backdropClickClose = this.dataset.backdropClose === "true";

            this.addEventListener("click", this.closeModal);

            if (this.backdropClickClose) {
                this.modal?.addEventListener("click", this.backdropClickHandler);
            }
        }

        backdropClickHandler = (event) => {
            if (event.target === this.modal) {
                this.closeModal();
            }
        }

        closeModal = () => {
            this.modal?.close();
        }

        disconnectedCallback() {
            this.removeEventListener("click", this.closeModal);

            if (this.backdropClickClose) {
                this.modal?.removeEventListener("click", this.backdropClickHandler);
            }
        }
    }

    class ArnSliderComponent extends HTMLElement {
        constructor() {
            super();
            this.slider = this.querySelector('[id^="Slider-"]');
            this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
            this.enableSliderLooping = false;
            this.currentPageElement = this.querySelector('.slider-counter--current');
            this.pageTotalElement = this.querySelector('.slider-counter--total');
            this.prevButton = this.querySelector('button[name="previous"]');
            this.nextButton = this.querySelector('button[name="next"]');
            this.slidesPerView = Number(this.dataset.slidesPerView);
            this.thumbnailWrapper = this.querySelector(".slider-thumbnails");
            this.thumbnailItems = this.querySelectorAll('[id^="Thumbnail-"]');
            this.thumbnailDragging = false;
            this.productAnchor = this.closest(".arn-product-card").querySelector(".tile-title > a");

            if (!this.slider || !this.nextButton) return;

            if (this.thumbnailItems.length < 2) {
                this.prevButton.disabled = true;
                this.nextButton.disabled = true;
                return
            }

            this.initPages();
            const resizeObserver = new ResizeObserver((entries) => this.initPages());
            resizeObserver.observe(this.slider);

            this.slider.addEventListener('scroll', this.update.bind(this));
            this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
            this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
            if (this.thumbnailWrapper && this.thumbnailItems.length > 0) {
                this.pointerScroll(this.thumbnailWrapper);
                this.thumbnailItems[0].dataset.selected = "true";
            }
            if (this.thumbnailItems.length > 0) this.thumbnailItems.forEach((thumbnail) => {
                thumbnail.addEventListener("click", (event) => {
                    this.onThumbnailClick(event, thumbnail);
                });
            })
        }

        initPages() {
            this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
            if (this.sliderItemsToShow.length < 2) return;
            this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
            this.slidesPerPage = this.slidesPerView > 0 ? this.slidesPerView : Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
            this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
            this.update();
        }

        resetPages() {
            this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
            this.initPages();
        }

        update() {
            // Temporarily prevents unneeded updates resulting from variant changes
            // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
            if (!this.slider || !this.nextButton) return;

            const previousPage = this.currentPage;
            this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

            if (this.currentPageElement && this.pageTotalElement) {
                this.currentPageElement.textContent = this.currentPage;
                this.pageTotalElement.textContent = this.totalPages;
            }

            if (this.currentPage != previousPage) {
                this.dispatchEvent(
                    new CustomEvent('slideChanged', {
                        detail: {
                            currentPage: this.currentPage,
                            currentElement: this.sliderItemsToShow[this.currentPage - 1],
                        },
                    })
                );
            }

            if (this.enableSliderLooping) return;

            if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
                this.prevButton.setAttribute('disabled', 'disabled');
            } else {
                this.prevButton.removeAttribute('disabled');
            }

            if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
                this.nextButton.setAttribute('disabled', 'disabled');
            } else {
                this.nextButton.removeAttribute('disabled');
            }
        }

        isSlideVisible(element, offset = 0) {
            const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
            return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
        }

        onButtonClick(event) {
            event.preventDefault();
            const step = event.currentTarget.dataset.step || 1;

            if (event.currentTarget.name === 'next') {
                this.slideScrollPosition = this.slider.scrollLeft + step * this.sliderItemOffset;
            } else {
                this.slideScrollPosition = this.slider.scrollLeft - step * this.sliderItemOffset;
            }

            // Update selected thumbnail
            if (this.thumbnailWrapper && this.thumbnailItems.length > 0) {
                const previousSelectedThumbnail = this.thumbnailWrapper.querySelector("[data-selected=true]");
                if (previousSelectedThumbnail) previousSelectedThumbnail.dataset.selected = "false";
                const currentSlide = this.thumbnailItems[Math.round(this.slideScrollPosition / this.sliderItemOffset)];
                if (currentSlide) {
                    currentSlide.dataset.selected = "true";
                    this.productAnchor.href = currentSlide.dataset.href;
                }
            }

            this.setSlidePosition(this.slideScrollPosition);
        }

        setSlidePosition(position) {
            this.slider.scrollTo({
                left: position,
            });
        }

        onThumbnailClick(event, thumbnail) {
            event.preventDefault();
            const thumbnailIndex = [...thumbnail.parentNode.children].indexOf(thumbnail);
            this.slideScrollPosition = thumbnailIndex * this.sliderItemOffset
            this.setSlidePosition(this.slideScrollPosition);
        }

        pointerScroll(elem) {
            let thumbnail;

            const dragStart = (ev) => {
                ev.preventDefault();
                elem.setPointerCapture(ev.pointerId);
                thumbnail = ev.target;
            }
            const dragEnd = (ev) => {
                ev.preventDefault();
                elem.releasePointerCapture(ev.pointerId);
                if (!this.thumbnailDragging && thumbnail) {
                    thumbnail.click();
                    this.thumbnailWrapper.querySelector("[data-selected=true]").dataset.selected = "false";
                    const currentSlide = this.thumbnailItems[Math.round(this.slideScrollPosition / this.sliderItemOffset)];
                    if (currentSlide) {
                        currentSlide.dataset.selected = "true";
                        this.productAnchor.href = currentSlide.dataset.href
                    }
                }
                this.thumbnailDragging = false;
            }
            const drag = (ev) => {
                ev.preventDefault();
                if (elem.hasPointerCapture(ev.pointerId)) {
                    elem.scrollLeft -= ev.movementX
                    this.thumbnailDragging = true;
                };
            }

            elem.addEventListener("pointerdown", dragStart);
            elem.addEventListener("pointerup", dragEnd);
            elem.addEventListener("pointermove", drag);
        };
    }

    /**
     * Product Card Slider Component with Swiper Integration
     * Handles product variant navigation with infinite loop support
     */
   class ArnProductCardSliderComponent extends HTMLElement {
    constructor() {
        super();

        // DOM element references — sliderItems is intentionally NOT cached here.
        // When this element is assembled in memory by ArnLoadMore (detached from
        // the live DOM), the constructor runs before all sibling slides have been
        // injected. Caching sliderItems at this point would give a stale NodeList.
        // initializeSwiper() always re-queries sliderItems so it sees the final
        // slide count after all siblings have been appended.
        this.slider = this.querySelector('[id^="Slider-"]');
        this.currentPageElement = this.querySelector('.slider-counter--current');
        this.pageTotalElement = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');

        // Card root element — used for setting data-active-variant on slide change.
        // The CSS rules defined in arn-plp-product-card.liquid use this attribute
        // to drive visibility of all variant-specific elements (badge, wishlist,
        // color label, product info). This replaces the previous mechanism that
        // toggled tw-hidden on 4 separate index families.
        this.cardRoot = this.closest('.arn-product-card');

        // Configuration
        this.slidesPerView = Number(this.dataset.slidesPerView) || 1;
        this.scrollDisabled = this.dataset.scrollDisabled === "true";

        // State
        this.isInitialized = false;
        this.swiper = null;

        // Exit early if disabled or missing required elements
        if (this.scrollDisabled || !this.slider || !this.nextButton) return;

        this.initializeSwiper();
    }

    initializeSwiper() {
        // Guard: defer initialisation if this card is still being assembled in
        // memory by ArnLoadMore. The attribute is removed and this method is
        // called again by replaceSkeleton() after DOM insertion.
        if (this.dataset.swiperPending === 'true') {
            // console.log('[ARN Slider] initializeSwiper deferred — data-swiper-pending is set');
            return;
        }

        // Wait for Swiper library to be available
        if (!window.Swiper) {
            setTimeout(() => this.initializeSwiper(), 50);
            return;
        }

        if (this.isInitialized) return;

        // Re-query sliderItems now, after all siblings may have been injected.
        // See class JSDoc for why this is not cached in the constructor.
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        const slideCount = this.sliderItems.length;

        // Cache cardRoot lookup in case the element was detached at construction time
        if (!this.cardRoot) this.cardRoot = this.closest('.arn-product-card');

        // console.log(`[ARN Slider] initializeSwiper — ${slideCount} slide(s) found`);

        const swiperConfig = {
            slidesPerView: this.slidesPerView,
            spaceBetween: 0,
            loop: slideCount > 1,
            allowTouchMove: true,
            grabCursor: slideCount > 1,
            centeredSlides: false,
            speed: 300,
            watchOverflow: true,

            navigation: {
                nextEl: this.nextButton,
                prevEl: this.prevButton,
            },

            on: {
                init: () => {
                    this.isInitialized = true;
                    this.updateUI();
                    this.updateButtonStates();
                },
                slideChange: (swiper) => {
                    this.updateUI();
                    this.updateButtonStates();

                    this.dispatchEvent(new CustomEvent('slideChanged', {
                        detail: {
                            currentPage: swiper.realIndex + 1,
                            currentElement: this.sliderItems[swiper.realIndex],
                            activeIndex: swiper.activeIndex,
                            realIndex: swiper.realIndex
                        },
                    }));
                },
                realIndexChange: (swiper) => {
                    this.updateProductCardInfo(swiper.realIndex);
                }
            }
        };

        try {
            this.swiper = new window.Swiper(this.slider, swiperConfig);

            if (this.prevButton) {
                this.prevButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (this.swiper) this.swiper.slidePrev();
                });
            }

            if (this.nextButton) {
                this.nextButton.addEventListener('click', (e) => {
                    e.preventDefault();
                    if (this.swiper) this.swiper.slideNext();
                });
            }

        } catch (error) {
            console.error('Error initializing Swiper for product card:', error);
            this.initializeFallback();
        }
    }

    /**
     * Update UI elements (counters, indicators).
     */
    updateUI() {
        if (!this.swiper || !this.isInitialized) return;

        const currentIndex = this.swiper.realIndex;
        const totalSlides = this.sliderItems.length;

        if (this.currentPageElement) {
            this.currentPageElement.textContent = currentIndex + 1;
        }
        if (this.pageTotalElement) {
            this.pageTotalElement.textContent = totalSlides;
        }
    }

    /**
     * Update navigation button states based on current position.
     */
    updateButtonStates() {
        if (!this.swiper || !this.isInitialized) return;

        if (this.swiper.params.loop) {
            if (this.prevButton) this.prevButton.removeAttribute('disabled');
            if (this.nextButton) this.nextButton.removeAttribute('disabled');
            return;
        }

        const isAtStart = this.swiper.isBeginning;
        const isAtEnd = this.swiper.isEnd;

        if (this.prevButton) {
            if (isAtStart) this.prevButton.setAttribute('disabled', 'disabled');
            else this.prevButton.removeAttribute('disabled');
        }
        if (this.nextButton) {
            if (isAtEnd) this.nextButton.setAttribute('disabled', 'disabled');
            else this.nextButton.removeAttribute('disabled');
        }
    }

    /**
     * Update product card information based on active slide.
     *
     * Collapsed from the previous 4-querySelectorAll-forEach implementation
     * to a single dataset assignment. The CSS rules in arn-plp-product-card.liquid
     * key off data-active-variant on the card root and show only the variant
     * elements (badge, wishlist, color, product info) whose data-variant-index
     * matches. No per-element class toggling needed.
     *
     * @param {number} slideIndex - Zero-based real slide index from Swiper
     */
    updateProductCardInfo(slideIndex) {
        const adjustedIndex = slideIndex + 1; // Convert to 1-based to match data-variant-index
        if (this.cardRoot) {
            this.cardRoot.dataset.activeVariant = String(adjustedIndex);
        }
    }

    /**
     * Legacy compatibility — wishlist visibility is now driven by the same
     * data-active-variant CSS rule as everything else. Kept as a no-op for
     * any external callers that still invoke it.
     *
     * @param {number} slideIndex - Zero-based slide index
     */
    updateWishlistButtons(slideIndex) {
        // Handled by updateProductCardInfo via CSS rule — no action needed.
    }

    // ── Fallback (when Swiper fails to load) ──────────────────────────────

    initializeFallback() {
        console.warn('Falling back to original slider system for product card');

        this.currentPage = 1;
        this.totalPages = this.sliderItems.length;

        if (this.currentPageElement) this.currentPageElement.textContent = this.currentPage;
        if (this.pageTotalElement) this.pageTotalElement.textContent = this.totalPages;

        if (this.prevButton) {
            this.prevButton.addEventListener('click', (e) => { e.preventDefault(); this.slideToPrev(); });
        }
        if (this.nextButton) {
            this.nextButton.addEventListener('click', (e) => { e.preventDefault(); this.slideToNext(); });
        }

        this.updateFallbackButtonStates();
        this.isInitialized = true;
    }

    slideToPrev() {
        this.currentPage = this.currentPage > 1 ? this.currentPage - 1 : this.totalPages;
        this.updateFallbackDisplay();
    }

    slideToNext() {
        this.currentPage = this.currentPage < this.totalPages ? this.currentPage + 1 : 1;
        this.updateFallbackDisplay();
    }

    updateFallbackDisplay() {
        if (this.currentPageElement) this.currentPageElement.textContent = this.currentPage;
        this.updateProductCardInfo(this.currentPage - 1);
        this.updateFallbackButtonStates();
    }

    updateFallbackButtonStates() {
        if (this.prevButton) this.prevButton.removeAttribute('disabled');
        if (this.nextButton) this.nextButton.removeAttribute('disabled');
    }

    // ── Public API ────────────────────────────────────────────────────────

    slideTo(index) {
        if (this.swiper) {
            this.swiper.slideTo(index);
        } else {
            this.currentPage = Math.max(1, Math.min(index + 1, this.totalPages));
            this.updateFallbackDisplay();
        }
    }

    getCurrentSlide() {
        return this.swiper ? this.swiper.realIndex : this.currentPage - 1;
    }

    destroy() {
        if (this.swiper) {
            this.swiper.destroy();
            this.swiper = null;
        }
        this.isInitialized = false;
    }

    // ── Compatibility ─────────────────────────────────────────────────────

    resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        if (this.swiper) {
            this.swiper.update();
        } else {
            this.totalPages = this.sliderItems.length;
            if (this.pageTotalElement) this.pageTotalElement.textContent = this.totalPages;
        }
    }

    update() {
        if (this.swiper) this.swiper.update();
        this.updateUI();
        this.updateButtonStates();
    }
}

    class ArnSwiper extends HTMLElement {
        connectedCallback() {
            this.config = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent ?? null);
            this.zoomInButton = this.querySelector("[data-zoom-in]");
            this.zoomOutButton = this.querySelector("[data-zoom-out]");
            if (this.config && this.config.init !== true) this.config.init = false;
            this.singleClickZoom = this.dataset.singleClickZoom === "true";

            // Setup single click zoom
            if (this.config.zoom || this.singleClickZoom) {
                this.config.on = {
                    click: function (swiper, event) {
                        const zoom = swiper.zoom;
                        if (zoom.scale && zoom.scale !== 1) {
                            zoom.out();
                        } else {
                            zoom.in(event.target);
                        }
                    },
                }
            }

            this.swiper = window.Swiper ? new window.Swiper(this, this.config) : null;
            this.control = this.dataset.control ? document.querySelector(this.dataset.control) : null;

            if (this.swiper) {
                this.swiper.on("afterInit", () => {
                    this.dispatchEvent(new CustomEvent('swiper:afterInit'))
                });

                this.swiper.on("slideChange", () => {
                    this.dispatchEvent(new CustomEvent("slideChanged", {
                        detail: {
                            activeIndex: this.swiper.activeIndex,
                            previousIndex: this.swiper.previousIndex,
                            realIndex: this.swiper.realIndex,
                        }
                    }));

                    if (this.zoomOutButton) this.zoomOutButton.disabled = true;
                    if (this.zoomInButton) this.zoomInButton.disabled = false;
                });

                this.zoomInButton?.addEventListener('click', () => {
                    this.swiper.zoom.in();
                    if (this.zoomInButton) this.zoomInButton.disabled = true;
                    if (this.zoomOutButton) this.zoomOutButton.disabled = false;
                });

                this.zoomOutButton?.addEventListener('click', () => {
                    this.swiper.zoom.out();
                    if (this.zoomOutButton) this.zoomOutButton.disabled = true;
                    if (this.zoomInButton) this.zoomInButton.disabled = false;
                });

                if (this.config && !this.config.init) {
                    this.swiper.init();
                    if (this.control && this.control.swiper) {
                        this.swiper.controller.control = this.control.swiper;
                    } else if (this.control) {
                        this.control.addEventListener('swiper:afterInit', () => {
                            this.swiper.controller.control = this.control.swiper
                            this.swiper.pagination.update();
                        });
                    }
                }

                this.classList.add('visible-swiper-slides');
                this.toggleAttribute("data-loaded", true);
            }
            else {
                console.error('Failed to load Swiper');
            }
        }

        disconnectedCallback() {
            this.swiper?.destroy();
        }
    };

    class ArnIntersectionSticky extends HTMLElement {
        connectedCallback() {
            this.placement = this.dataset.placement;
            this.hideTarget = document.getElementById(this.dataset.hideTarget);
            this.threshold = parseFloat(this.dataset.threshold || "0");

            this.observer = new IntersectionObserver(this.intersectionCallback.bind(this), {
                root: null,
                rootMargin: "0px",
                threshold: this.threshold
            });

            if (this.hideTarget) {
                this.observer.observe(this.hideTarget);
            }
        }

        disconnectedCallback() {
            this.observer?.disconnect();
        }

        canShow(targetRect) {
            const elementRect = this.getBoundingClientRect();

            switch (this.placement) {
                case "bottom":
                    return targetRect.top > elementRect.top;
                case "top":
                    return targetRect.bottom < elementRect.bottom;
                default:
                    return true;
            }

        }

        intersectionCallback(entries) {
            entries.forEach((entry) => {
                const targetRect = entry.target.getBoundingClientRect();
                !entry.isIntersecting && this.canShow(targetRect) ? this.show() : this.hide();
            });
        }

        hide() {
            this.style.display = "none";
        }

        show() {
            this.style.removeProperty("display");
        }
    };

    class ArnDeliveryEstimator extends HTMLElement {
        connectedCallback() {
            const configEstimationDays = [];

            this.zip = this.dataset.zip;
            this.country = this.dataset.country;
            this.province = this.dataset.province;
            this.appendText = this.dataset.appendText;
            this.hideYear = this.dataset.hideYear === "true";
            this.orderDate = this.dataset.orderDate;
            this.skipWeekends = this.dataset.skipWeekends !== "false"; // default true

            this.fetchUrl = `${window.routes?.cart_url}/shipping_rates.json`;
            this.localeCountry = `${window.Shopify?.locale || "en"}-${window.Shopify?.country || "US"}`;

            this.dataset.estimationDays?.split("-")?.forEach(day => {
                const dayN = parseInt(day.trim());

                if (!isNaN(dayN) && dayN >= 0) {
                    configEstimationDays.push(dayN);
                }
            });

            if (configEstimationDays.length > 0) {
                this.updateDeliveryEstimate(configEstimationDays);
            } else if (this.zip && this.country && this.province) {
                this.estimateDelivery();
            }
        }

        /**
         * Adds `days` working days to `baseDate`, skipping Saturday (6) and Sunday (0).
         * If skipWeekends is false, behaves like a simple addDate.
         */
        addBusinessDays(baseDate, days) {
            const date = new Date(baseDate);

            if (!this.skipWeekends) {
                date.setDate(date.getDate() + days);
                return date;
            }

            let remaining = days;

            while (remaining > 0) {
                date.setDate(date.getDate() + 1);
                const dow = date.getDay();
                // 0 = sunday, 6 = saturday
                if (dow !== 0 && dow !== 6) {
                    remaining--;
                }
            }

            return date;
        }

        estimateDelivery() {
            const params = new URLSearchParams();

            params.append("shipping_address[zip]", this.zip);
            params.append("shipping_address[country]", this.country);
            params.append("shipping_address[province]", this.province);

            fetch(`${this.fetchUrl}?${params.toString()}`, { method: "GET" })
                .then(response => {
                    if (!response.ok) {
                        throw new Error("Error:", response.status);
                    }

                    return response.json();
                })
                .then(data => {
                    const shippingRates = data.shipping_rates || [];
                    const deliveryDays = shippingRates[0]?.delivery_days;

                    this.updateDeliveryEstimate(deliveryDays);
                })
                .catch(error => {
                    console.error("Error fetching delivery estimate", error);
                });
        }

        updateDeliveryEstimate(days = []) {
            const baseDate = this.orderDate ? new Date(this.orderDate) : new Date();

            const dateFormatOptions = {
                month: "long",
                day: "numeric",
            };

            if (!this.hideYear) {
                dateFormatOptions.year = "numeric";
            }

            let textOut = days.map(day => {
                const date = this.addBusinessDays(baseDate, day);

                return new Intl.DateTimeFormat(this.localeCountry, dateFormatOptions).format(date);
            }).join(" - ");

            if (this.appendText) {
                textOut += ` ${this.appendText}`;
            }

            this.textContent = textOut;
        }
    }

    class ArnScrollProgress extends HTMLElement {
        connectedCallback() {
            this.progressEl = this.querySelector("[data-scroll-progress]");
            this.container = document.querySelector(this.dataset.scrollContainer);

            if (!this.progressEl) return;

            this.updateScrollBar();
            window.addEventListener('scroll', this.updateScrollBar);
        };

        updateScrollBar = () => {
            this.containerHeight = this.container ? this.container.offsetHeight : window.innerHeight;
            const scrollTop = window.scrollY;
            const scrollHeight = document.documentElement.scrollHeight - this.containerHeight;
            const scrollPercent = (scrollTop / scrollHeight) * 100;

            this.progressEl.style.height = scrollPercent > 100 ? "100%" : scrollPercent + '%';
        }
    };

    class ArnDarkToggle extends HTMLElement {
        connectedCallback() {
            this.darkMode = this.dataset.darkMode;
            this.darkModeToggle = this.querySelector(`[data-dark-mode-toggle="${this.darkMode}"]`);
            this.storageKey = 'arn-user-dark-mode-preferred';

            // Sync with current state
            this.syncWithCurrentState();

            // Attach click event listener to toggle button
            this.darkModeToggle?.addEventListener("click", this.toggleDarkMode.bind(this));
        }

        syncWithCurrentState() {
            try {
                const stored = localStorage.getItem(this.storageKey);
                const isCurrentlyDark = document.documentElement.classList.contains(this.darkMode);

                // Ensure consistency between localStorage and DOM state
                if (stored === null && isCurrentlyDark) {
                    localStorage.setItem(this.storageKey, 'true');
                    console.log('Initial dark mode preference saved');
                }

                if (stored !== null) {
                    console.log(`Dark mode state synced: ${stored === 'true'}`);
                }
            } catch (e) {
                console.warn('localStorage is not available:', e);
            }
        }

        toggleDarkMode() {
            if (this.dataset.darkMode) {
                // Toggle dark mode class on documentElement
                const isDarkMode = document.documentElement.classList.toggle(this.dataset.darkMode);

                // Save preference to localStorage
                try {
                    localStorage.setItem(this.storageKey, isDarkMode.toString());
                    console.log(`Dark mode toggled: ${isDarkMode}`);
                } catch (e) {
                    console.warn('Unable to save to localStorage:', e);
                }
            }
        }
    }
    class ArnRTRViewerList extends HTMLElement {
        constructor() {
            super();

            // Container
            this.sectionId = this.dataset.sectionId;
            this.container = this.querySelector(`#rtr-container-${this.sectionId}`);

            if (this.container) {
                this.desktopBackground = this.container.dataset.desktopBackground;
                this.mobileBackground = this.container.dataset.mobileBackground;
                this.defaultBackground = 'https://rtr-viewer.luxottica.com/asset/background/gradient-square-srgb.jpg'
            }

            // Changers
            this.rtrChangers = this.querySelectorAll('.rtr-changer');
            if (this.rtrChangers.length > 1) {
                this.defaultProduct = Array.from(this.rtrChangers).find(input => input.checked);
                if (this.defaultProduct) {
                    this.defaultProductUpc = this.defaultProduct.value;
                }

                this.rtrChangers.forEach((input) => input.addEventListener('change', this.handleChange.bind(this)));
            }
            this.swiperElement = this.querySelector(`.swiper-${this.sectionId}`);

            // Blocks
            this.rtrBlockTitle = this.querySelector('.rtr-block-title-wrapper');
            this.rtrBlockText = this.querySelector('.rtr-block-text-wrapper');
            this.rtrBlocCta = this.querySelector('.rtr-cta--block');

            // CTA Update
            this.ctaToUpdate = this.querySelector('.rtr-product--cta');

            // Resize event listener
            this.handleResize = this.handleResize.bind(this);
            window.addEventListener('resize', this.handleResize);

            // Init object with settings
            this.initInput = {
                data: {
                    selector: `#${this.container.id}`,
                    id: {
                        type: 'upc', // moco or token
                        value: `${this.defaultProductUpc}`, // or any other valid UPC, MoCo or token
                    },
                    backgroundPath: this.defaultBackground,
                    settings: {
                        autoResetCamera: true,
                        autoRotateCamera: false,
                        autoRotateSpeed: 20,
                        backgroundFit: 'fill',
                        boundingArea: {
                            topLeft: {
                                x: 0,
                                y: 0,
                            },
                            bottomRight: {
                                x: 1,
                                y: 0.8,
                            },
                        },
                        boundingFit: 'auto',
                        cameraAnimationTime: 0.3,
                        cameraRotationInitial: {
                            phi: 0,
                            theta: 0,
                        },
                        cameraRotationReset: {
                            phi: 0,
                            theta: 0,
                        },
                        clearColor: '#ffffff',
                        gestures: {
                            mouse: {
                                left: 'rotate',
                                middle: 'none',
                                right: 'pan',
                                wheel: 'none',
                            },
                            touches: {
                                one: 'rotate',
                                two: 'dolly-pan',
                                three: 'none',
                            },
                        },
                        lensesName: undefined,
                        lensesArnacity: 1,
                        orbitPoint: true,
                        qualityMoving: 4,
                        qualityStill: 5,
                        scrollInteraction: false,
                        showBackground: true,
                        showEnvironment: false,
                        transmission: true,
                    }
                },
                callbacks: {
                    onRendered: () => this.handleOnRendered()
                },
            };

            if (window.innerWidth >= 1024) {
                // Desktop BoundingArea
                this.initInput.data.settings.boundingArea.topLeft.x = 0.5;
                // Desktop BackgroundPath
                if (this.desktopBackground) {
                    this.initInput.data.backgroundPath = this.desktopBackground;
                }
            } else {
                // Mobile BackgroundPath
                if (this.mobileBackground) {
                    this.initInput.data.backgroundPath = this.mobileBackground;
                }
            }
        }

        connectedCallback() {
            window.addEventListener('DOMContentLoaded', () => {
                const rtrViewer = window.rtrViewer;
                //rtrViewer.init(this.initInput);

                this.swiper = new Swiper(this.swiperElement, {
                    slidesPerView: 'auto',
                    slidesPerGroupAuto: true,
                    centerInsufficientSlides: true,
                    spaceBetween: 16,
                    updateOnWindowResize: true,
                    loop: false,
                    grabCursor: true
                });
            });
        }

        handleOnRendered() {
            if (this.rtrChangers.length > 1) {
                this.rtrChangers.forEach((input) => {
                    if (input.disabled) {
                        input.removeAttribute('disabled');
                    }
                });
            }
        }

        handleChange(event) {
            const selectedValue = event.target.value;
            // Disable Changers to avoid more than one switch at a time before rendering
            if (this.rtrChangers.length > 1) {
                this.rtrChangers.forEach((input) => {
                    if (!input.disabled) {
                        //input.setAttribute('disabled', '');
                        input.removeAttribute('checked');
                    }
                });
            }

            event.target.setAttribute('checked', '');

            this.handleInfoChange(event.currentTarget);

            // Update CTA
            if (this.ctaToUpdate) {
                this.ctaToUpdate.innerHTML = event.target.dataset.productTitle
                this.ctaToUpdate.closest('a').setAttribute('title', event.target.dataset.productTitle);
                this.ctaToUpdate.closest('a').setAttribute('href', event.target.dataset.variantUrl);
            }
            // Update upc ID
            if (selectedValue) {
                rtrViewer.setId({
                    type: 'upc',
                    value: `${event.target.value}`,
                });
            }
        }

        handleInfoChange(element) {
            if (element.dataset.blockTitle != '') {
                this.rtrBlockTitle.innerHTML = element.dataset.blockTitle;
                this.rtrBlockTitle.classList.remove('tw-hidden');
            } else {
                this.rtrBlockTitle.classList.add('tw-hidden');
            }

            if (element.dataset.blockText != '') {
                this.rtrBlockText.innerHTML = element.dataset.blockText;
                this.rtrBlockText.classList.remove('tw-hidden');
            } else {
                this.rtrBlockText.classList.add('tw-hidden');
            }

            if (this.rtrBlocCta) {
                this.updateCta(
                    this.rtrBlocCta,
                    element.dataset.blockCtaLabel,
                    element.dataset.blockCtaUrl
                );
            }
        }

        updateCta(ctaElement, label, url) {
            const hiddenClass = 'tw-hidden';
            const hasValidData = label && url;

            if (hasValidData) {
                ctaElement.setAttribute('href', url);
                ctaElement.setAttribute('title', label);
                ctaElement.innerHTML = label;
                ctaElement.classList.remove(hiddenClass);
            } else {
                ctaElement.classList.add(hiddenClass);
            }
        }

        // Update BoundingArea and BackgroundPath on resize
        handleResize() {
            if (window.innerWidth >= 1024) {
                // Desktop BoundingArea
                rtrViewer.setBoundingArea({
                    topLeft: {
                        x: 0.5,
                        y: 0,
                    },
                    bottomRight: {
                        x: 1,
                        y: 1,
                    },
                });
                // Desktop BackgroundPath
                if (this.desktopBackground) {
                    rtrViewer.setBackgroundPath(this.desktopBackground);
                } else {
                    rtrViewer.setBackgroundPath(this.defaultBackground);
                }
            } else {
                // Mobile BoundingArea
                rtrViewer.setBoundingArea({
                    topLeft: {
                        x: 0,
                        y: 0,
                    },
                    bottomRight: {
                        x: 1,
                        y: 1,
                    },
                });

                // Mobile BackgroundPath
                if (this.mobileBackground) {
                    rtrViewer.setBackgroundPath(this.mobileBackground);
                } else {
                    rtrViewer.setBackgroundPath(this.defaultBackground);
                }
            }
        }
    }

    class ArnDialog extends HTMLElement {
        connectedCallback() {
            this.isFullscreen = this.classList.contains("arn-modal-fullscreen");
            this.querySelectorAll(".js-arn-modal-close").forEach(btn => btn.addEventListener("click", this.close));
            this.isAppearFromRight = this.dataset.appearFromRight === "true";
        }

        showModal = () => {
            this.dataset.open = "true";
            this.classList.remove("tw-invisible", "tw-opacity-0");
            if (this.isFullscreen) {
                document.body.style.overflow = "hidden";
            }
            if (this.isAppearFromRight) {
                this.classList.remove("tw-translate-x-full");
            }
        }

        close = () => {
            this.dataset.open = "false";
            this.classList.add("tw-invisible", "tw-opacity-0");
            if (this.isFullscreen) {
                document.body.style.overflow = "";
            }
            if (this.isAppearFromRight) {
                this.classList.add("tw-translate-x-full");
            }
        }

    }

    class ArnSizeSwitcher extends HTMLElement {
        constructor() {
            super();
            this.unitElement = document.querySelector('.unit-measurement');
            this.first = false;
        }

        connectedCallback() {
            this.mmRadio = this.querySelector('#mm');
            this.inchRadio = this.querySelector('#inch');

            this.mmLabel = this.querySelector('label[for="mm"]').querySelector('.knob');
            this.inchLabel = this.querySelector('label[for="inch"]').querySelector('.knob');

            this.mmRadio.addEventListener('change', this.updateToggleState);
            this.inchRadio.addEventListener('change', this.updateToggleState);
            this.first = true;
            this.updateToggleState(this.first);
        }

        updateToggleState = (status) => {
            if (this.mmRadio.checked && status != true) {
                this.mmLabel.classList.add('tw-bg-arn-default');
                this.inchLabel.classList.remove('tw-bg-arn-default');
                this.updateUnitMeasurement('mm');
            } else if (this.inchRadio.checked && status != true) {
                this.inchLabel.classList.add('tw-bg-arn-default');
                this.mmLabel.classList.remove('tw-bg-arn-default');
                this.updateUnitMeasurement('inch');
            } else if (status == true) {
                this.mmLabel.classList.add('tw-bg-arn-default');
                this.inchLabel.classList.remove('tw-bg-arn-default');
            }
        }

        updateUnitMeasurement(unit) {
            let sizeValues = document.querySelectorAll('.current-value');
            sizeValues.forEach((sizeValue) => {
                var currentValue = parseFloat(sizeValue.dataset.value);
                if (currentValue != null && currentValue != '') {
                    if (unit == 'inch') {
                        currentValue = currentValue / 25.4;
                        sizeValue.dataset.value = currentValue;
                        sizeValue.dataset.unit = unit;
                        sizeValue.innerHTML = currentValue.toFixed(2) + unit;
                    } else {
                        currentValue = currentValue * 25.4;
                        sizeValue.dataset.value = currentValue.toFixed(0);
                        sizeValue.dataset.unit = unit;
                        sizeValue.innerHTML = currentValue.toFixed(0) + unit;
                    }
                }
            }
            )
        }

        disconnectedCallback() {
            this.mmRadio.removeEventListener('change', this.updateToggleState);
            this.inchRadio.removeEventListener('change', this.updateToggleState);
        }
    }

    class ArnAccordion extends HTMLElement {
        connectedCallback() {
            this.details = this.querySelector('.arn-accordion-item');
            this.closedIcon = this.querySelector('.arn-icon-closed');
            this.openIcon = this.querySelector('.arn-icon-open');

            if (!this.details || !this.closedIcon || !this.openIcon) return;

            // Initialize icon state based on open status
            this.updateIcons();

            // Listen to toggle event
            this.details.addEventListener('toggle', this.toggleHandler);
        }

        disconnectedCallback() {
            this.details?.removeEventListener('toggle', this.toggleHandler);
        }

        toggleHandler = () => {
            this.updateIcons();
        }

        updateIcons() {
            if (this.details.open) {
                this.closedIcon.classList.add('tw-hidden');
                this.openIcon.classList.remove('tw-hidden');
            } else {
                this.closedIcon.classList.remove('tw-hidden');
                this.openIcon.classList.add('tw-hidden');
            }
        }
    }

    class ArnTryOnButton extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.addEventListener('click', (event) => {
                publish(PUB_SUB_EVENTS.startTryOn, {
                    source: 'try-on-pdp'
                });
            });

            this.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    this.click();
                }
            });
        }
    }

    class VMMVPdp extends HTMLElement {
        constructor() {
            super();

            // Container
            this.container = this.querySelector(`.vmmv-container`);
            this.buttonTryOn = document.querySelector('[data-button-tryon]');
            this.upcSelected = this.container.dataset.upcSelected;
            this.upcList = [];
            this.onlyUpcList = [];
            this.wishlist = [];
            this.testWishlist = [];
            this.prodIdVariantId = [];
            this.resultTransitions = [];
            this.isAddingToBag = false;
            // Allineato a settings.separate_cart_items: quando attivo ogni aggiunta
            // deve restare una riga a se'. Default true se l'attributo manca.
            this.separateCartItems = true;
            this.arnWishlist = document.querySelector('arn-wishlist');
            this.VMInitParamsKey = 'dc9ab158-7aa6-4ef2-bcbb-fad4a0591d4a'

            this.VMInitParams = {
                'source': 'VMMV_AN',
                'key': this.VMInitParamsKey,
                'channel': 'ecommerce',
                'brand': 'an',
            };

            this.region = 'EU'
            this.locale = Shopify.locale + '-' + Shopify.country
            if (this.locale == 'en-US' || this.locale == 'es-US') {
                this.region = 'US'
            }
            if (this.locale == 'en-AU' || this.locale == 'en-NZ') {
                this.region = 'AU'
            }

            this.store = {
                'id': '',
                'type': '',
                region: this.region,
            };

            if (this.container) {

                if (this.container.dataset.separateCartItems !== undefined) {
                    this.separateCartItems = this.container.dataset.separateCartItems === 'true';
                }

                this.upcList = this.container.dataset.upcList.split('||');
                this.upcList.forEach(stringUpc => {

                    this.dettUpc = stringUpc.split('#_#');
                    this.brand = {
                        'name': this.dettUpc[5],
                        'logoUrl': this.dettUpc[6]
                    };

                    this.onlyUpcList.push(this.dettUpc[0]);

                    this.prodIdVariantId[this.dettUpc[1]] = {
                        'product': this.dettUpc[13],
                        'handle': this.dettUpc[14]
                    };

                    this.testWishlist[this.dettUpc[1]] = {
                        'product_id': this.dettUpc[13],
                        'variant_id': this.dettUpc[1],
                        'handle': this.dettUpc[14],
                        'upc': this.dettUpc[0],
                    }
                });
            }
        }

        connectedCallback() {

            this.checkUpcAvailability(this.onlyUpcList, this.upcSelected).then(result => {
                this.resultTransitions = result;
                if (this.resultTransitions[this.upcSelected]?.available == true) {
                    this.buttonTryOn.classList.remove('tw-hidden');
                } else {
                    this.buttonTryOn.remove();
                }
            });


            this.tryOnUnsubscriber = subscribe(PUB_SUB_EVENTS.startTryOn, (event) => {
                if (event.source != "try-on-pdp") {
                    return;
                }

                if (this.container) {
                    this.prices = [];
                    this.prodList = [];
                    this.upcList.forEach(stringUpc => {
                        this.dettUpc = stringUpc.split('#_#');
                        if (this.resultTransitions[this.dettUpc[0]].available == true) {
                            let prodItem = {
                                'upc': this.dettUpc[0],
                                'code': this.dettUpc[1],
                                'name': this.dettUpc[2],
                                'colorCode': this.dettUpc[3],
                                'thumbnailUrl': this.dettUpc[4],
                                'brand': this.brand,
                                'size': this.dettUpc[9],
                                'lensColorLabel': this.dettUpc[8],
                                'frameColorLabel': this.dettUpc[7]
                            };

                            if (this.resultTransitions[this.dettUpc[0]].transitions == true) {
                                prodItem.isTransition = true;
                                prodItem.transitionLens = {
                                    defaultColor: 'SIGNATURE-GEN8-AMBER',
                                    defaultType: 'SIGNATURE',
                                    defaultAlphaLevel: 0.5,
                                    availableColors: [
                                        'SIGNATURE-GEN8-AMBER',
                                        'SIGNATURE-GEN8-AMETHYST',
                                        'SIGNATURE-GEN8-EMERALD',
                                        'SIGNATURE-GEN8-GRAY',
                                        'SIGNATURE-GEN8-SAPPHIRE',
                                        'SIGNATURE-GEN8-BROWN',
                                        'SIGNATURE-GEN8-GGREEN'
                                    ]
                                };

                            }

                            this.prodList.push(prodItem);
                            this.prices.push({
                                upc: this.dettUpc[0],
                                currency: this.dettUpc[12],
                                current: { text: this.dettUpc[10], value: parseFloat(this.dettUpc[10]) },
                                ...(this.dettUpc[10] != this.dettUpc[11] && this.dettUpc[11] > 0 ? {
                                    previous: { text: this.dettUpc[11], value: parseFloat(this.dettUpc[11]) }
                                } : {})
                            });
                        }
                    });
                }
                // Init object with settings
                this.initInput = {
                    'selector': '.vmmv-container',
                    'locale': this.locale,
                    'products': this.prodList,
                    'store': this.store,
                    'prices': this.prices,
                    'vmInit': this.VMInitParams,
                    'style': '',
                    fromStore: true,
                    isTryOnEnabled: true,
                    isPictureModeEnabled: true,
                    isUploadPictureEnabled: true,
                    isVideoModeEnabled: true,
                    isTakeScreenshotEnabled: true
                };

                console.log('this.initInput', this.initInput)
                let elem = document.querySelector('arn-pdp-vmmv');
                if (elem) {
                    elem.classList.remove('tw-hidden');
                    elem.style.background = "var(--Alpha-Black-Dark, rgba(0, 0, 0, 0.50))";
                    elem.style.backdropFilter = "blur(11px)";
                    document.querySelector('body').style.overflowY = "hidden";
                }
                let vmapp = null

                Object.values(this.testWishlist).forEach(variantWish => {
                    const isInWishlist = this.arnWishlist?.isItemInWishlist({
                        dataset: {
                            productId: variantWish['product_id'],
                            variantId: variantWish['variant_id'],
                        },
                        handle: variantWish['handle']
                    });
                    if (isInWishlist) { this.wishlist.push(variantWish['upc']); }
                });

                const config = {
                    ...this.initInput,
                    transitionFeature: {
                        enabled: true
                    },
                    onClose: async () => {
                        if (elem) {
                            elem.classList.add('tw-hidden');
                            elem.style.background = "";
                            elem.style.backdropFilter = "";
                        }
                        await vmapp.unmount();
                        console.log("chiudo");
                        document.querySelector('body').style.overflowY = "";
                        this.container.innerHTML = '';
                    },
                    onPrivacyReject: async () => {
                        await config.onClose();
                        await vmapp.unmount();
                    },
                    onAddToBag: async (product, frameSize) => {

                        // Il mirror resta aperto dopo l'aggiunta e la cart notification
                        // compare dietro l'overlay: senza questo blocco i click ripetuti
                        // partono tutti.
                        if (this.isAddingToBag) return;
                        this.isAddingToBag = true;

                        // add to cart
                        let formData = {
                            'id': product.code,
                            'quantity': 1,
                        };
                        if (this.separateCartItems) {
                            // Deve stare dentro properties: /cart/add.js ignora in silenzio
                            // le chiavi top-level sconosciute e le righe identiche si fondono
                            // in una sola a quantita' 2.
                            formData.properties = { '_cart_added_at': Date.now() };
                        }
                        try {
                            let url_fetch = window.Shopify.routes.root + 'cart/add.js';
                            url_fetch = url_fetch + '?sections=arn-cart-notification-product,cart-icon-bubble,arn-cart-notification-button';

                            const response = await fetch(url_fetch, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify(formData)
                            });
                            const responseData = await response.json();
                            if (response.ok) {
                                this.cart = document.querySelector('cart-notification');
                                this.cart.classList.remove('is-empty');
                                this.cart.renderContents({
                                    key: responseData.key,
                                    sections: responseData.sections,
                                });

                                this.cart.classList.add('active', 'animate');
                                //await config.onClose();
                            } else {
                                console.log('Error:', responseData.message);
                            }

                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: product.code,
                                productHandle: this.prodIdVariantId[product.code].handle,
                                cartData: responseData
                            });
                        } catch (error) {
                            console.error('Error:', error);
                        } finally {
                            this.isAddingToBag = false;
                        }
                    },
                    wishlistedUpcs: this.wishlist,
                    onToggleWishlist: async (product) => {
                        const wishlistItem = {
                            dataset: {
                                productId: this.prodIdVariantId[product.code]['product'],
                                variantId: product.code
                            },
                            handle: this.prodIdVariantId[product.code]['handle']
                        };
                        this.arnWishlist.toggleWishlistItem(wishlistItem);
                    }
                }

                vmapp = new vmmv.VMWidgetApp()
                console.log('vmapp', vmapp)

                vmmv.warmUp();
                vmapp.setConfig(config)
                    .then(() => {
                        vmapp.init()
                    })
                    .catch(e => {
                        console.error(e)
                    })
            });

        }

        async checkUpcAvailability(upcs, upcSelected) {
            // All configuration params are written below
            let resultTransitions = [];
            let upcAvailability = await vmmv.isUPCSupported(upcs, this.VMInitParamsKey)
            let isBrowserSupported = await vmmv.isBrowserSupported();
            let browserSupported = isBrowserSupported ? 1 : 0;
            let UpcSupported = 0;

            Object.entries(upcAvailability).forEach(([upc, upcAvailabilityResponse]) => {
                resultTransitions[upc] = {
                    transitions: upcAvailabilityResponse.transitions,
                    available: upcAvailabilityResponse.available
                };
                if (upcAvailabilityResponse.available == true && upc == upcSelected) {
                    UpcSupported = 1;
                    if (browserSupported == 1) {
                        document.querySelector('[data-button-tryon]').classList.remove('tw-hidden');
                    }
                }
            });
            publish(PUB_SUB_EVENTS.VMMVAvailability, {
                'Vm_IsUpcSupported': `${UpcSupported}`,
                'Vm_IsBrowserSupported': `${browserSupported}`,
                'Vm_PageContext': 'PDP',
            });

            return resultTransitions;

        }
    }

    class ArnProductPrice extends HTMLElement {
        connectedCallback() {
            window.arnTealiumPrices = window.arnTealiumPrices || [];
            this.pdHandle = this.dataset.arnProductHandle;
            this.extractPrice();
        }

        extractPrice() {
            if (!this.pdHandle) return;

            const priceNode = this.querySelector('[data-arn-price]') || this;
            const baseCents = parseInt(priceNode.dataset?.arnDkBaseCents, 10);

            let sale;
            let full;

            // Discount Kit resolves the displayed sale/compare client-side when its engine is
            // loaded (cookie/code discounts); otherwise read the price the server rendered.
            const display = window.ArnDkStorefrontPrices?.resolveDisplayFromNode?.(priceNode);

            if (display && display.priceText) {
                sale = GeneralUtils.parseCurrency(display.priceText).value;
                full = display.compareText ? GeneralUtils.parseCurrency(display.compareText).value : sale;
            } else {
                const saleEl = priceNode.querySelector('.arn-product-rich-card-price, .tw-typo-arn-base');
                const compareEl = priceNode.querySelector('.arn-product-rich-card-compare, .tw-line-through');
                if (saleEl) sale = GeneralUtils.parseCurrency(saleEl.textContent.trim()).value;
                if (compareEl) full = GeneralUtils.parseCurrency(compareEl.textContent.trim()).value;
                if (!Number.isFinite(sale) && Number.isFinite(baseCents)) sale = baseCents / 100;
            }

            if (!Number.isFinite(sale)) return;
            if (!Number.isFinite(full) || full < sale) full = sale;

            const priceObj = { productHandle: this.pdHandle, price: sale, priceFull: full };
            if (!window.arnTealiumPrices.find(item => item.productHandle === this.pdHandle)) {
                window.arnTealiumPrices.push(priceObj);
            }
        }
    }

    function setupCustomElements() {
        if (!customElements.get('arn-video-with-thumbnail')) {
            customElements.define("arn-video-with-thumbnail", ArnVideoWithThumbnail);
        }

        if (!customElements.get('arn-form')) {
            customElements.define("arn-form", ArnForm);
        }

        if (!customElements.get('arn-newsletter-form')) {
            customElements.define("arn-newsletter-form", ArnNewsletterForm);
        }

        if (!customElements.get('arn-password-checker')) {
            customElements.define("arn-password-checker", ArnPasswordChecker);
        }

        if (!customElements.get('arn-quantity-input')) {
            customElements.define("arn-quantity-input", ArnQuantitySelector);
        }

        if (!customElements.get("arn-tabs")) {
            customElements.define("arn-tabs", ArnTabs);
        }

        if (!customElements.get("arn-rangeslider")) {
            customElements.define("arn-rangeslider", ArnRangeSlider);
        }

        if (!customElements.get('arn-text-field')) {
            customElements.define("arn-text-field", ArnTextField);
        }

        if (!customElements.get('arn-slideshow')) {
            customElements.define("arn-slideshow", ArnSlideShow);
        }

        if (!customElements.get('arn-countdown')) {
            customElements.define("arn-countdown", ArnCountdown);
        }

        if (!customElements.get("arn-modal-trigger")) {
            customElements.define("arn-modal-trigger", ArnModalTrigger);
        }

        if (!customElements.get('arn-slider-component')) {
            customElements.define('arn-slider-component', ArnSliderComponent);
        }

        if (!customElements.get('arn-product-card-slider-component')) {
            customElements.define('arn-product-card-slider-component', ArnProductCardSliderComponent);
        }

        if (!customElements.get('arn-swiper')) {
            customElements.define('arn-swiper', ArnSwiper);
        }

        if (!customElements.get('arn-password-checker-container')) {
            customElements.define("arn-password-checker-container", ArnPasswordCheckerContainer);
        }

        if (!customElements.get("arn-modal-closer")) {
            customElements.define("arn-modal-closer", ArnModalCloser);
        }

        if (!customElements.get("arn-intersection-sticky")) {
            customElements.define("arn-intersection-sticky", ArnIntersectionSticky);
        }

        if (!customElements.get("arn-delivery-estimator")) {
            customElements.define("arn-delivery-estimator", ArnDeliveryEstimator);
        }

        if (!customElements.get('arn-scroll-progress')) {
            customElements.define("arn-scroll-progress", ArnScrollProgress);
        }

        if (!customElements.get("arn-dark-toggle")) {
            customElements.define("arn-dark-toggle", ArnDarkToggle);
        }

        if (!customElements.get("arn-rtr-viewer-list")) {
            customElements.define("arn-rtr-viewer-list", ArnRTRViewerList);
        }

        if (!customElements.get("arn-dialog")) {
            customElements.define("arn-dialog", ArnDialog);
        }

        if (!customElements.get('arn-size-switcher')) {
            customElements.define("arn-size-switcher", ArnSizeSwitcher);
        }

        if (!customElements.get('arn-accordion')) {
            customElements.define("arn-accordion", ArnAccordion);
        }

        if (!customElements.get('arn-try-on-button')) {
            customElements.define("arn-try-on-button", ArnTryOnButton);
        }

        if (!customElements.get('arn-pdp-vmmv')) {
            customElements.define('arn-pdp-vmmv', VMMVPdp);
        }

        if (!customElements.get('arn-product-price')) {
            customElements.define('arn-product-price', ArnProductPrice);
        }
    }

    function setupListeners() {
        document.querySelectorAll(".js-arn-modal").forEach((modal) => {
            modal.addEventListener("click", (event) => {
                const isBackdrop = event.target === modal;
                const backdropClickClose = modal.getAttribute("data-backdrop-close") === "true";

                if (isBackdrop && backdropClickClose && typeof modal.close === "function") {
                    modal.close();
                }
            });
        });

        document.querySelectorAll(".js-arn-modal-close").forEach((btn) => {
            btn.addEventListener("click", () => {
                btn.closest("dialog")?.close();

                const parentId = btn.getAttribute("data-close-parent");
                if (parentId) {
                    const parent = document.getElementById(parentId);
                    if (parent && typeof parent.close === "function") parent.close();
                }
            });
        });

        document.querySelectorAll(".js-arn-modal-btn").forEach((btn) => {
            btn.addEventListener("click", () => {
                const modalId = btn.getAttribute("data-modal-id");
                const target = modalId ? document.getElementById(modalId) : null;
                const submenuModalId = btn.getAttribute("data-submenu-modal-id") || false;
                const submenuTarget = submenuModalId ? document.getElementById(submenuModalId) : null;

                if (target && typeof target.showModal === "function") target.showModal();

                if (submenuTarget && typeof submenuTarget.showModal === "function") submenuTarget.showModal();
            });
        });

        document.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
            if (checkbox.getAttribute("data-indeterminate") === "true") {
                checkbox.indeterminate = true;
            }
        });

        document.querySelectorAll(".js-arn-draggable").forEach((elmnt) => {
            let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

            const dragMouseDown = (e) => {
                e.preventDefault();

                pos3 = e.clientX;
                pos4 = e.clientY;

                document.addEventListener("mouseup", closeDragElement);
                document.addEventListener("mousemove", elementDrag);
            }

            const elementDrag = (e) => {
                e.preventDefault();

                pos1 = pos3 - e.clientX;
                pos2 = pos4 - e.clientY;
                pos3 = e.clientX;
                pos4 = e.clientY;

                elmnt.style.top = (elmnt.offsetTop - pos2) + "px";
                elmnt.style.left = (elmnt.offsetLeft - pos1) + "px";
                elmnt.style.position = "absolute";
            }

            const closeDragElement = () => {
                document.removeEventListener("mouseup", closeDragElement);
                document.removeEventListener("mousemove", elementDrag);
            }

            const restorePos = (e) => {
                e.preventDefault();

                elmnt.style.removeProperty("position");
                elmnt.style.removeProperty("top");
                elmnt.style.removeProperty("left");
            }

            elmnt.addEventListener("mousedown", dragMouseDown);
            elmnt.addEventListener("contextmenu", restorePos);
        });

        document.querySelectorAll(".js-customerLogin").forEach((form) => {
            form.addEventListener("submit", async (event) => {
                const fdata = new FormData(form);
                const submitButtons = form.querySelectorAll("[type='submit']");
                const storefrontUrl = form.getAttribute("data-storefront-url");
                const storefrontToken = fdata.get("storefront_token");

                const input = {
                    email: fdata.get("customer[email]"),
                    password: fdata.get("customer[password]")
                }

                event.preventDefault();
                submitButtons.forEach(btn => btn.disabled = true);

                try {
                    const response = await fetch(storefrontUrl, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Shopify-Storefront-Access-Token": storefrontToken
                        },
                        body: JSON.stringify({
                            query: `#graphql
                                mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
                                    customerAccessTokenCreate(input: $input) {
                                        customerAccessToken {
                                            accessToken
                                        }
                                        customerUserErrors {
                                            message
                                        }
                                    }
                                }
                            `,
                            variables: { input }
                        })
                    });

                    const result = await response.json();
                    const token = result.data?.customerAccessTokenCreate?.customerAccessToken;

                    if (result.error) {
                        console.error(result.error);
                    }

                    if (token) {
                        sessionStorage.setItem("customerAccessToken", token.accessToken);
                    }
                }
                catch (error) {
                    console.error(error);
                }

                form.submit();
            });
        });

        document.querySelectorAll(".js-showPickerLb").forEach((lb) => {
            lb.addEventListener("click", () => {
                const targetId = lb.getAttribute("for");
                const targetEl = targetId ? document.getElementById(targetId) : null;

                if (targetEl && targetEl.showPicker && !targetEl.disabled && !targetEl.readOnly) {
                    targetEl.showPicker();
                }
            });
        });
    }


    setupCustomElements();
    setupListeners();
}

arnComponents();